import { useState, useEffect } from "react";
import "@style/Admin.scss";
import "@style/sass/_accounting.scss";
import RightTimeApi from "@/API";
import Helpers from "@/Helpers";
import { toast } from "react-toastify";
import BillingCategoriesTab from "./BillingCategoriesTab";
import FeeScheduleTab from "./FeeScheduleTab";
import InvoicesTab from "./InvoicesTab";
import PaymentsTab from "./PaymentsTab";
import AccruingInvoicesTab from "./AccruingInvoicesTab";
import PendingApprovalInvoicesTab from "./PendingApprovalInvoicesTab";
import GeneralLedgerTab from "./GeneralLedgerTab";
import AccountingDashboardTab from "./AccountingDashboardTab";
import BillingOrgsTab from "./BillingOrgsTab";
import InvoiceDetailModal from "@elements/modals/InvoiceDetailModal";
import {
    getBillingOrgIdFromUrl,
    loadAccountingBillingOrgContextAsync,
    type AccountingBillingOrgContext,
} from "@/utils/billingOrgHelpers";

export default function AdminBillingTab(props) {

    const { updateSpinner, activeTab, adminPerms, company } = props.tabData;

    function getTabFromUrl() {
        if (typeof window === 'undefined') return "dashboard";
        const params = new URLSearchParams(window.location.search);
        return params.get("billingTab") || "dashboard";
    }

    const [activeAccountingTab, setActiveAccountingTab] = useState(getTabFromUrl());
    const [previewInvoice, setPreviewInvoice] = useState(null);
    const [previewMode, setPreviewMode] = useState(false);
    const [billingOrgContext, setBillingOrgContext] = useState<AccountingBillingOrgContext | null>(null);
    const [selectedBillingOrgId, setSelectedBillingOrgId] = useState(getBillingOrgIdFromUrl());

    const [refreshKey, setRefreshKey] = useState(0);
    function handleDataChanged() {
        setRefreshKey(k => k + 1);
    }

    function formatCurrency(amount) {
        return Helpers.formatCurrency(amount);
    }

    useEffect(() => {
        if (!activeTab || activeTab !== "accounting") return;
        loadAccountingBillingOrgContextAsync().then(setBillingOrgContext);
    }, [activeTab, refreshKey]);

    useEffect(() => {
        if (typeof window === 'undefined') return;
        if (!activeTab || activeTab !== "accounting") return;
        const params = new URLSearchParams(window.location.search);
        params.set("billingTab", activeAccountingTab);
        if (selectedBillingOrgId > 0) {
            params.set("billingOrgId", String(selectedBillingOrgId));
        } else {
            params.delete("billingOrgId");
        }
        const newUrl = window.location.pathname + '?' + params.toString();
        window.history.replaceState({}, '', newUrl);
    }, [activeAccountingTab, selectedBillingOrgId, activeTab]);

    async function processInvoiceAsync(invoiceId, action, companyId) {
        if (action === "preview") {
            setPreviewMode(true);
            setPreviewInvoice({ id: invoiceId, companyId });
            return;
        }

        if (action === "generate") {
            updateSpinner(true);
            try {
                const resp = await RightTimeApi.postAsync(`/Invoice/${invoiceId}/Close`, {});
                if (resp && resp.ok) {
                    toast.success("Invoice closed for approval");
                    handleDataChanged();
                } else {
                    toast.error("Failed to close invoice for approval");
                }
            } catch (error) {
                toast.error("Error closing invoice: " + error.message);
            } finally {
                updateSpinner(false);
            }
        }
    }

    function handlePendingApprovalClick(invoice) {
        setPreviewMode(false);
        setPreviewInvoice({ id: invoice.id, companyId: invoice.companyId });
    }

    function handlePreviewModalClose() {
        setPreviewInvoice(null);
        setPreviewMode(false);
        handleDataChanged();
    }

    function isOnThisTab() {
        return activeTab === "accounting";
    }

    const orgOptions = billingOrgContext?.billingOrgs ?? [];
    const showOrgSelector = orgOptions.length > 1 || billingOrgContext?.accessibleBillingOrgIds != null;
    const canManageBillingOrgs = billingOrgContext?.canManageBillingOrgs ?? false;

    return (
        isOnThisTab() && (
            <div className="accountingTab">
                <div className="accountingSidebar">
                    {showOrgSelector && (
                        <div className="accountingOrgFilter">
                            <label htmlFor="accounting-billing-org-filter">Billing org</label>
                            <select
                                id="accounting-billing-org-filter"
                                value={selectedBillingOrgId || ""}
                                onChange={(e) => setSelectedBillingOrgId(parseInt(e.target.value, 10) || 0)}
                            >
                                <option value="">All billing orgs</option>
                                {orgOptions.map((org) => (
                                    <option key={org.id} value={org.id}>{org.name}</option>
                                ))}
                            </select>
                        </div>
                    )}
                    <div className="accountingMenu">
                    <button type="button" data-accounting-tab="dashboard" className={activeAccountingTab === "dashboard" ? "active" : ""} onClick={() => setActiveAccountingTab("dashboard")}>Dashboard</button>
                    {canManageBillingOrgs && (
                        <button type="button" data-accounting-tab="billingOrgs" className={activeAccountingTab === "billingOrgs" ? "active" : ""} onClick={() => setActiveAccountingTab("billingOrgs")}>Billing Orgs</button>
                    )}
                    <button type="button" data-accounting-tab="categories" className={activeAccountingTab === "categories" ? "active" : ""} onClick={() => setActiveAccountingTab("categories")}>Billing Categories</button>
                    <button type="button" data-accounting-tab="fees" className={activeAccountingTab === "fees" ? "active" : ""} onClick={() => setActiveAccountingTab("fees")}>Fee Schedule</button>
                    <button type="button" data-accounting-tab="accruing" className={activeAccountingTab === "accruing" ? "active" : ""} onClick={() => setActiveAccountingTab("accruing")}>Accruing</button>
                    <button type="button" data-accounting-tab="pendingApproval" className={activeAccountingTab === "pendingApproval" ? "active" : ""} onClick={() => setActiveAccountingTab("pendingApproval")}>Pending Approval</button>
                    <button type="button" data-accounting-tab="invoices" className={activeAccountingTab === "invoices" ? "active" : ""} onClick={() => setActiveAccountingTab("invoices")}>Invoices</button>
                    <button type="button" data-accounting-tab="payments" className={activeAccountingTab === "payments" ? "active" : ""} onClick={() => setActiveAccountingTab("payments")}>Payments</button>
                    <button type="button" data-accounting-tab="ledger" className={activeAccountingTab === "ledger" ? "active" : ""} onClick={() => setActiveAccountingTab("ledger")}>General Ledger</button>
                    </div>
                </div>
                <div className="accountingContent">
                    {activeAccountingTab === "dashboard" && (
                        <AccountingDashboardTab
                            isActive={activeAccountingTab === "dashboard"}
                            updateSpinner={updateSpinner}
                            refreshKey={refreshKey}
                            billingOrgId={selectedBillingOrgId}
                            onDataChanged={handleDataChanged}
                        />
                    )}
                    {activeAccountingTab === "billingOrgs" && canManageBillingOrgs && (
                        <BillingOrgsTab
                            isActive={activeAccountingTab === "billingOrgs"}
                            updateSpinner={updateSpinner}
                            refreshKey={refreshKey}
                        />
                    )}
                    {activeAccountingTab === "categories" && (
                        <BillingCategoriesTab
                            isActive={activeAccountingTab === "categories"}
                            onToggleCollapse={() => {}}
                            updateSpinner={updateSpinner}
                        />
                    )}
                    {activeAccountingTab === "fees" && (
                        <FeeScheduleTab
                            isActive={activeAccountingTab === "fees"}
                            updateSpinner={updateSpinner}
                        />
                    )}
                    {activeAccountingTab === "accruing" && (
                        <AccruingInvoicesTab
                            isActive={activeAccountingTab === "accruing"}
                            processInvoiceAsync={processInvoiceAsync}
                            formatCurrency={formatCurrency}
                            updateSpinner={updateSpinner}
                            refreshKey={refreshKey}
                            billingOrgId={selectedBillingOrgId}
                        />
                    )}
                    {activeAccountingTab === "pendingApproval" && (
                        <PendingApprovalInvoicesTab
                            isActive={activeAccountingTab === "pendingApproval"}
                            onInvoiceClick={handlePendingApprovalClick}
                            formatCurrency={formatCurrency}
                            updateSpinner={updateSpinner}
                            refreshKey={refreshKey}
                            billingOrgId={selectedBillingOrgId}
                            onDataChanged={handleDataChanged}
                        />
                    )}
                    {activeAccountingTab === "invoices" && (
                        <InvoicesTab
                            isActive={activeAccountingTab === "invoices"}
                            updateSpinner={updateSpinner}
                            billingOrgId={selectedBillingOrgId}
                        />
                    )}
                    {activeAccountingTab === "payments" && (
                        <PaymentsTab
                            isActive={activeAccountingTab === "payments"}
                            updateSpinner={updateSpinner}
                            billingOrgId={selectedBillingOrgId}
                        />
                    )}
                    {activeAccountingTab === "ledger" && (
                        <GeneralLedgerTab
                            isActive={activeAccountingTab === "ledger"}
                            updateSpinner={updateSpinner}
                        />
                    )}
                </div>
                {previewInvoice && (
                    <InvoiceDetailModal
                        invoiceId={previewInvoice.id}
                        companyId={previewInvoice.companyId}
                        previewMode={previewMode}
                        onClose={handlePreviewModalClose}
                        updateSpinner={updateSpinner}
                    />
                )}
            </div>
        )
    );
}
